import { useSetState } from './store/use-set-state.js';
import { BaseReturnType } from '../../../core/color-rule/types.js';
import { LegendItem } from '../../../core/components/legend/categorical/types/legend.js';
import {
  ColorPalette,
  CustomColorPalette,
} from '../../../core/types/color-palette.js';
import { MultiMeterBarSegmentProps } from '../types/multi-meter-bar-chart.js';
import {
  MultiMeterBarChartSegmentData,
  MultiMeterBarChartState,
} from '../types/state.js';
import { buildSegmentData } from '../utils/build-segment-data.js';
import { getSegmentColor } from '../utils/get-segment-color.js';

export function useMultiMeterBarStore() {
  const setState = useSetState();

  const initSegmentsState = (
    data: MultiMeterBarSegmentProps[],
    seriesForColorRule: Array<{
      value: number | Record<string, unknown>;
      [key: string]: unknown;
    }>,
    colorPalette: ColorPalette | CustomColorPalette,
    colorBuilder: {
      for: (series: {
        value: number | Record<string, unknown>;
        [key: string]: unknown;
      }) => BaseReturnType;
    },
  ): Omit<MultiMeterBarChartState, 'tooltip'> => {
    const segments = data.map((segmentData, index) => {
      const colorAppliedByRule = colorBuilder.for(seriesForColorRule[index]);
      const color = getSegmentColor(
        segmentData,
        colorPalette,
        index,
        colorAppliedByRule,
      );
      return buildSegmentData(segmentData, color);
    });

    const visibleSegments = new Map(
      segments.map((segment) => [
        segment.id,
        { ...segment, highlighted: true },
      ]),
    );

    return { segments, visibleSegments };
  };

  const setVisibleSegments = (visibleSegments: LegendItem[]) => {
    setState((prev) => {
      const { segments, visibleSegments: visibleSegmentsStore } = prev;

      const segmentsWithIds: Record<string, MultiMeterBarChartSegmentData> =
        segments.reduce(
          (acc, curr) => ({ ...acc, [curr.id]: { ...curr } }),
          {},
        );

      visibleSegmentsStore.clear();

      visibleSegments.forEach((segment) =>
        visibleSegmentsStore.set(segment.id, {
          ...segmentsWithIds[segment.id],
        }),
      );

      return {
        ...prev,
        visibleSegments: visibleSegmentsStore,
      };
    });
  };

  const setHighlightedSegment = (highlightedSegment?: LegendItem) => {
    setState((prev) => ({ ...prev, highlightedSegment }));
  };

  return {
    initSegmentsState,
    setHighlightedSegment,
    setVisibleSegments,
  };
}
